import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useResize from '../../hooks/use-resize';
import s from './modal.module.css';
import { getElementPseudoContent, getElementSize, getScrollWidth, browser } from '../../utils/dom';
export const context = createContext({
    close: () => Promise.resolve(),
    closable: false,
    mobile: false,
    size: 'm',
});
function measureTextNode(node) {
    const range = document.createRange();
    range.selectNodeContents(node);
    const rects = range.getClientRects();
    return rects[0].height;
}
const margin = (raw) => Math.max(0, parseInt(raw, 10));
export const origin = 8;
const limit = 24;
const padding = 48;
const paddingMobile = 24;
const TEXT_NODE = 3;
export function calculateScrollAdjustments() {
    const rect = document.body.getBoundingClientRect();
    return {
        bodyOverflow: Math.round(rect.left + rect.right) < window.innerWidth,
        scrollWidth: getScrollWidth(),
    };
}
export function useScrollShift(state, scrollable) {
    const bodyPadding = useRef(browser ? document.body.style.paddingRight : '0');
    const scrollPadding = useMemo(() => {
        if (!browser || state !== 'show' || !scrollable) {
            return null;
        }
        const modalOverflow = scrollable.scrollHeight > document.documentElement.clientHeight;
        const rect = document.body.getBoundingClientRect();
        const bodyOverflow = Math.round(rect.left + rect.right) < window.innerWidth;
        const scrollWidth = getScrollWidth();
        return (bodyOverflow && !modalOverflow) || (!bodyOverflow && modalOverflow) ? scrollWidth : null;
    }, [state, scrollable]);
    useEffect(() => {
        document.body.style.paddingRight = bodyPadding.current;
        if (!scrollPadding) {
            return;
        }
        const calculatedValue = Number.parseFloat(window.getComputedStyle(document.body).paddingRight);
        document.body.style.paddingRight = `${calculatedValue + scrollPadding}px`;
    }, [scrollPadding]);
    return scrollPadding;
}
export function useMobile(el, enabled) {
    const [mobile, setMobile] = useState(false);
    const checkMobile = useCallback(() => {
        if (!el.current) {
            return;
        }
        setMobile(getElementPseudoContent(el.current) == 'xs');
    }, [el.current]);
    useEffect(() => checkMobile(), [checkMobile]);
    useResize({ handler: checkMobile, debounce: 200, enabled: !!enabled });
    return mobile;
}
export function useFit({ scrollable, content, enabled, mobile }) {
    const [fit, setFit] = useState(true);
    const checkFit = useCallback(() => {
        if (!scrollable.current || !content.current) {
            return;
        }
        const availableHeight = getElementSize(scrollable.current).height;
        const contentHeight = Array.from(content.current.childNodes || []).reduce((res, item) => {
            const text = item.nodeType === TEXT_NODE;
            if (!text && item.classList.contains(s.close)) {
                return res;
            }
            const style = (!text && window.getComputedStyle(item));
            const height = text ? measureTextNode(item) : parseInt(style.height, 10);
            const margins = text ? 0 : margin(style.marginTop) + margin(style.marginBottom);
            return res + height + margins;
        }, 0);
        setFit(availableHeight - (contentHeight + (mobile ? paddingMobile : padding)) >= 0);
    }, [scrollable.current, content.current, mobile]);
    useEffect(() => checkFit(), [checkFit]);
    useResize({ handler: checkFit, debounce: 200, enabled: !!enabled });
    return fit;
}
export function position(scrollable) {
    const current = scrollable.scrollTop;
    if (current <= limit) {
        return origin;
    }
    const offset = current - limit;
    return origin + offset;
}
//# sourceMappingURL=utils.js.map